/**
* @copyright Copyright (C) 2021 Nile AI, Inc - All Rights Reserved
* Unauthorized copying of this file, via any medium is strictly prohibited
* Proprietary and confidential
*/

import React from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import Box from '@material-ui/core/Box';
import TextField from '@material-ui/core/TextField';
import FormHelperText from '@material-ui/core/FormHelperText';
import { styled } from '@material-ui/core/styles';
import palette from 'styles/colors';

const contrastTextField = palette.silver.silver1;

/** Select mode props to align the menu under the field. */
const SelectProps = {
  MenuProps: {
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'left',
    },
    transformOrigin: {
      vertical: 'top',
      horizontal: 'left',
    },
    getContentAnchorEl: null,
    PaperProps: {
      style: {
        maxHeight: 170,
      },
    },
  },
};

/** Use the 'hidden' property when you want to have the TextField in the DOM, but not visible */
const hiddenTextFieldProps = {
  paddingBottom: 0,
  height: 0,
  width: 0,
  overflow: 'hidden',
};

export const KnTextFieldBase = ({ children, ...rest }) => {
  let selectProps = {};
  if (rest.select) {
    SelectProps.multiple = rest.multiple;
    SelectProps.renderValue = rest.renderValue;
    selectProps = { SelectProps };
  }

  if (rest.select && rest.fullMaxHeight) {
    SelectProps.MenuProps.PaperProps.style.maxHeight = 510;
  }

  return (
    <TextField
      color="secondary"
      variant="filled"
      {...selectProps}
      {...rest}
    >
      {children}
    </TextField>
  );
};

/**
 * Component for text input.
 * Also see `KnContrastTextField` and `KnContrastTextField` for
 *  some styled `KnTextField` inputs.
 */
const KnTextField = (props) => {
  const {
    children,
    error,
    disableErrorMessage,
    hidden,
    pb,
    ...rest
  } = props;
  const { t: translate } = useTranslation();
  const { type: errorType } = error;

  /** Use the error's message if it was provided OR use a global field validation message. */
  const helperText = errorType
    ? (error.message || translate(`FIELD_VALIDATION_MESSAGES.${errorType}`))
    : null;

  return (
    <Box pb={pb} style={hidden ? hiddenTextFieldProps : { }}>
      <KnTextFieldBase
        fullWidth
        error={!!error.type}
        {...rest}
      >
        {children}
      </KnTextFieldBase>
      {/* Instead of using the helperText prop on TextField which dynamically adds the
          FormHelperText component when there is an error, we'll manually add it so it
          will be in the DOM all the time to prevent the fields from being pushed down.
          Fixes: https://kokoon.atlassian.net/browse/KK-250

          Note: We can't know beforehand how much vertical space an error message needs.
          We've set a min height of 22px for FormHelperText; this is enough for a single
          line error message. If an error message spans multiple lines, then it will push
          the fields down. */}
      {!disableErrorMessage && (
        <FormHelperText error data-testid="field-validation-error">{helperText}</FormHelperText>
      )}
    </Box>
  );
};

export const KnContrastTextField = styled(KnTextField)({
  '& .MuiInputBase-root': {
    backgroundColor: contrastTextField,

    '&.Mui-disabled': {
      opacity: 0.4,
    },
  },
});

/** Some text fields don't have a label so we need to center the values. */
export const KnUnlabeledTextField = styled(KnContrastTextField)({
  '& .MuiInputBase-input': {
    transform: 'translateY(-8px)',
    /**
     * Fixes KK-1172: Autofill style is overflowing the field.
     * By default, Material-UI uses labels for fields. As we need
     * unlabeled textfields, in order to align the input value with
     * an input adornment or the arrow of a select dropdown, we applied
     * a translateY of -8px transformation. However, this slightly moves
     * the input outside of the .MuiInputBase-root container. The sum of
     * paddingTop and marginTop is 27 to preserve the default paddingTop
     * of a textfield input. Decreasing the padding top will bring the input
     * in the bounds of the container.
     */
    paddingTop: 10,
    marginTop: 17,
  },
  '& .MuiSelect-filled': {
    backgroundColor: 'transparent',
    transform: 'translateY(-8px)',
  },
});

KnUnlabeledTextField.defaultProps = {
  label: '',
};

KnTextFieldBase.propTypes = {
  children: PropTypes.node,
};

KnTextField.propTypes = {
  children: PropTypes.node,
  /** Label for the input. Moves up, when field has a value */
  label: PropTypes.string,
  /** Validation error object, to show error under the input.
   * Displays `message` when given,
   * or translates `FIELD_VALIDATION_MESSAGES.type` otherwise.
   */
  error: PropTypes.shape({
    type: PropTypes.string,
    message: PropTypes.string,
  }),
  /** When `true`, error message is not displayed, only the red line */
  disableErrorMessage: PropTypes.bool,
  hidden: PropTypes.bool,
  pb: PropTypes.number,
};

KnTextFieldBase.defaultProps = {
  children: null,
};

KnTextField.defaultProps = {
  children: null,
  error: {},
  label: '',
  disableErrorMessage: false,
  hidden: false,
  pb: 3,
};

export default KnTextField;
